import {
  DEFAULT_ENV,
  envLabel,
  type MapDef,
  type MapPoi,
  type RaidEnv,
  type Vec3,
} from "@rietz/shared";
import { EXTRACT_EDGE_COLOR, MATERIAL_COLORS } from "../render/palette.js";
import { cssHex, cssHexScaled, poiColor } from "../render/poiPalette.js";

/**
 * The town map screen — **M**: a full-screen plan view of the whole map with
 * the extracts, the named buildings and an arrow for you.
 *
 * Plain DOM, not React: a canvas painted once per map and then touched by a
 * single arrow. Two stacked canvases — `base` (every solid, extract and POI,
 * painted once per map and on resize) and `live` (transparent, the player
 * arrow only, clearing the few hundred pixels the last arrow occupied). The
 * game keeps running and the pointer stays locked while it is open; the root
 * is `pointer-events: none`. Without a DOM every entry point is a no-op.
 */

// --- Layout ----------------------------------------------------------------

/** The square canvas takes this much of the viewport's shorter side. */
const SCREEN_FRACTION = 0.92;

/** Fraction of the canvas kept clear around the map bounds, per edge. */
const MARGIN = 0.05;

/** Backing-store resolution is capped the same way the WebGL renderer caps it. */
const MAX_PIXEL_RATIO = 2;

// --- Colours ---------------------------------------------------------------

/** Behind the map plate: the "paper" the plan is printed on. */
const PAPER_COLOR = "#0d1117";
/** The playable area itself, under every solid. */
const GROUND_COLOR = "#1b2129";
const BOUNDS_STROKE = "rgba(150,175,205,0.35)";
const CHROME_COLOR = "#cfdae6";
const CHROME_DIM_COLOR = "rgba(207,218,230,0.55)";
/** Every label is knocked out of its background with this halo. */
const TEXT_HALO = "rgba(8,11,15,0.85)";
const PLAYER_FILL = "#f4f8fc";
const PLAYER_STROKE = "#0b0f14";
const EXTRACT_FILL = "rgba(79,224,138,0.16)";

/**
 * A solid's plan fill is darkened toward this at ground level and released to
 * full colour by `BUILDING_HEIGHT_M` — the cheapest substitute for shaded
 * relief.
 */
const GROUND_SHADE = 0.5;
const BUILDING_HEIGHT_M = 7;

// --- Sizes, in units of side/1000 px ---------------------------------------

const PAD_U = 16;
const POI_DOT_U = 3.4;
const POI_TEXT_U = 13;
const EXTRACT_TEXT_U = 14;
const TITLE_TEXT_U = 21;
const CHROME_TEXT_U = 13;
/** Extract zones are a few metres across on a kilometre map: never smaller. */
const MIN_EXTRACT_U = 11;
const PLAYER_LEN_U = 15;
const PLAYER_WIDTH_U = 9;

/** Scale-bar lengths worth printing, metres. */
const SCALE_STEPS = [10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000] as const;

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** One solid, ready to paint: plan-view rectangle plus its shaded fill. */
interface PlanRect {
  x: number;
  y: number;
  w: number;
  h: number;
  top: number;
  fill: string;
}

export class TownMap {
  /** All null without a DOM; every method then returns immediately. */
  private readonly root: HTMLDivElement | null = null;
  /** The sized wrapper the two canvases are stretched over. */
  private readonly frame: HTMLDivElement | null = null;
  private readonly base: HTMLCanvasElement | null = null;
  private readonly live: HTMLCanvasElement | null = null;

  private map: MapDef | null = null;
  /**
   * The raid's time of day and weather, printed under the map's name. A CLEAR
   * DAY until a `welcome` says otherwise — the renderer's default too.
   */
  private env: RaidEnv = DEFAULT_ENV;
  private isOpen = false;

  /**
   * Set whenever the base layer no longer matches the map or viewport. `layout`
   * returns on it before touching `window`.
   */
  private dirty = true;

  /** Canvas backing-store side, device pixels. 0 until the first layout. */
  private sidePx = 0;
  /** Canvas CSS side, layout pixels. */
  private cssSidePx = 0;

  // World → canvas projection. North is −Z, i.e. up the screen.
  private scale = 1;
  private centerX = 0;
  private centerZ = 0;

  /** What the arrow covered last frame, so the live layer clears cheaply. */
  private lastArrow: Rect | null = null;

  private disposed = false;

  constructor() {
    if (typeof document === "undefined") return;
    const body: HTMLElement | null = document.body;
    if (body === null) return;

    const root = document.createElement("div");
    root.id = "town-map";
    root.setAttribute("aria-hidden", "true");
    const s = root.style;
    s.position = "fixed";
    s.inset = "0";
    s.display = "none";
    s.alignItems = "center";
    s.justifyContent = "center";
    s.background = "rgba(4, 7, 11, 0.78)";
    // Above the scrim (20): the map must be readable whether or not the pointer
    // happens to be locked. Never interactive — see the class comment.
    s.zIndex = "30";
    s.pointerEvents = "none";
    s.setProperty("user-select", "none");

    const frame = document.createElement("div");
    frame.style.position = "relative";
    frame.style.width = "0px";
    frame.style.height = "0px";

    const base = document.createElement("canvas");
    const live = document.createElement("canvas");
    for (const c of [base, live]) {
      c.style.position = "absolute";
      c.style.inset = "0";
      c.style.width = "100%";
      c.style.height = "100%";
      c.style.display = "block";
      frame.appendChild(c);
    }
    base.style.borderRadius = "3px";
    base.style.boxShadow = "0 8px 40px rgba(0,0,0,0.55)";

    root.appendChild(frame);
    body.appendChild(root);

    this.root = root;
    this.frame = frame;
    this.base = base;
    this.live = live;

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("resize", this.onResize);
  }

  get open(): boolean {
    return this.isOpen;
  }

  /**
   * Points the map at a map, or `null` when there is no world. Safe to call
   * with the same map again; only a genuine change repaints.
   */
  setMap(map: MapDef | null): void {
    if (this.map === map) return;
    this.map = map;
    this.dirty = true;
    this.clearLive();
    // Nothing to draw and nothing to be open on: a plan view of the raid the
    // player just walked out of is not a map of anywhere they are.
    if (map === null) {
      this.close();
      return;
    }
    // Painted NOW rather than on the first M press: 24 000 solids is a visible
    // hitch, better spent inside the map-load pause.
    this.layout();
  }

  /**
   * The raid's environment. Repaints the base layer, where the label lives —
   * only ever on a `welcome`.
   */
  setEnv(env: RaidEnv): void {
    if (env.time === this.env.time && env.weather === this.env.weather) return;
    this.env = { time: env.time, weather: env.weather };
    this.dirty = true;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.show();
  }

  show(): void {
    // No map, nothing to show. M in the menu is not a request for an empty box.
    if (this.isOpen || this.root === null || this.map === null) return;
    this.isOpen = true;
    this.root.style.display = "flex";
    this.root.setAttribute("aria-hidden", "false");
  }

  close(): void {
    if (!this.isOpen || this.root === null) return;
    this.isOpen = false;
    this.root.style.display = "none";
    this.root.setAttribute("aria-hidden", "true");
    this.clearLive();
  }

  /**
   * One frame of map; costs nothing while closed. `pos` is the RENDERED
   * position and `yaw` the raw look angle, so the arrow tracks the camera.
   */
  update(pos: Readonly<Vec3>, yaw: number): void {
    if (!this.isOpen || this.map === null) return;
    this.layout();
    this.drawPlayer(pos, yaw);
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.close();
    if (typeof window !== "undefined") {
      window.removeEventListener("keydown", this.onKeyDown);
      window.removeEventListener("resize", this.onResize);
    }
    this.root?.remove();
    this.map = null;
  }

  // ---------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------

  private readonly onKeyDown = (ev: KeyboardEvent): void => {
    if (ev.code !== "KeyM") return;
    // Held M must not strobe the map, and Ctrl/Cmd+M is a browser command.
    if (ev.repeat || ev.altKey || ev.ctrlKey || ev.metaKey) return;
    // There is no text entry in the game today. There will be (chat), and a
    // player typing "Kommt jemand?" must not be shown a map per M.
    if (isTextEntry(ev.target)) return;
    ev.preventDefault();
    this.toggle();
  };

  private readonly onResize = (): void => {
    // Only the flag: `update` picks it up while open, an unopened map on its
    // next map or open.
    this.dirty = true;
  };

  // ---------------------------------------------------------------------
  // Layout
  // ---------------------------------------------------------------------

  /** Sizes the canvases to the viewport and repaints the base layer if needed. */
  private layout(): void {
    if (!this.dirty) return;
    const map = this.map;
    const base = this.base;
    const live = this.live;
    const frame = this.frame;
    if (map === null || base === null || live === null || frame === null) return;
    this.dirty = false;

    const short = Math.min(window.innerWidth, window.innerHeight);
    const cssSide = Math.max(64, Math.floor(short * SCREEN_FRACTION));
    const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
    const sidePx = Math.max(64, Math.round(cssSide * ratio));

    if (cssSide !== this.cssSidePx) {
      this.cssSidePx = cssSide;
      frame.style.width = `${cssSide}px`;
      frame.style.height = `${cssSide}px`;
    }
    if (sidePx !== this.sidePx) {
      this.sidePx = sidePx;
      base.width = sidePx;
      base.height = sidePx;
      live.width = sidePx;
      live.height = sidePx;
    }

    // Fit the longer world span, so the plan is never stretched.
    const b = map.bounds;
    const span = Math.max(b.max.x - b.min.x, b.max.z - b.min.z);
    this.scale = span > 0 ? (sidePx * (1 - 2 * MARGIN)) / span : 1;
    this.centerX = (b.min.x + b.max.x) * 0.5;
    this.centerZ = (b.min.z + b.max.z) * 0.5;

    this.drawBase(map);
    this.clearLive();
  }

  private projX(x: number): number {
    return this.sidePx * 0.5 + (x - this.centerX) * this.scale;
  }

  /** +Z is south, so it grows DOWN the screen and north ends up at the top. */
  private projY(z: number): number {
    return this.sidePx * 0.5 + (z - this.centerZ) * this.scale;
  }

  // ---------------------------------------------------------------------
  // Base layer
  // ---------------------------------------------------------------------

  private drawBase(map: MapDef): void {
    const ctx = this.base?.getContext("2d") ?? null;
    if (ctx === null) return;

    const side = this.sidePx;
    const u = side / 1000;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, side, side);
    ctx.fillStyle = PAPER_COLOR;
    ctx.fillRect(0, 0, side, side);

    // --- Playable area -----------------------------------------------------
    const b = map.bounds;
    const bx = this.projX(b.min.x);
    const by = this.projY(b.min.z);
    const bw = this.projX(b.max.x) - bx;
    const bh = this.projY(b.max.z) - by;
    ctx.fillStyle = GROUND_COLOR;
    ctx.fillRect(bx, by, bw, bh);

    // --- Terrain surface: one heightfield, painted as horizontal runs of its
    // material map rather than one canvas call per cell.
    const terrain = map.terrain;
    if (terrain !== undefined) {
      const nx = terrain.width - 1;
      const nz = terrain.depth - 1;
      for (let j = 0; j < nz; j++) {
        let i = 0;
        while (i < nx) {
          const id = terrain.surfaceIds[j * nx + i] as number;
          let end = i + 1;
          while (end < nx && terrain.surfaceIds[j * nx + end] === id) end++;
          const surface = terrain.surfaces[id];
          const rgb = surface?.colorTint ?? MATERIAL_COLORS[surface?.material ?? "dirt"];
          ctx.fillStyle = cssHexScaled(rgb, GROUND_SHADE);
          const x0 = this.projX(terrain.origin.x + i * terrain.cellSize);
          const x1 = this.projX(terrain.origin.x + end * terrain.cellSize);
          const y0 = this.projY(terrain.origin.z + j * terrain.cellSize);
          const y1 = this.projY(terrain.origin.z + (j + 1) * terrain.cellSize);
          ctx.fillRect(x0, y0, Math.max(1, x1 - x0), Math.max(1, y1 - y0));
          i = end;
        }
      }
    }

    // --- Solids, in ascending order of box top: pads first, buildings on top,
    // so the plan view falls out of paint order.
    const rects: PlanRect[] = [];
    const groundY = b.min.y;
    for (const solid of map.solids) {
      const box = solid.box;
      const x0 = this.projX(box.min.x);
      const y0 = this.projY(box.min.z);
      const w = this.projX(box.max.x) - x0;
      const h = this.projY(box.max.z) - y0;
      if (!(w > 0) || !(h > 0)) continue;

      const top = box.max.y - groundY;
      const t = clamp01(top / BUILDING_HEIGHT_M);
      const rgb = solid.colorTint ?? MATERIAL_COLORS[solid.material];
      rects.push({
        x: x0,
        y: y0,
        // A wall one pixel wide still has to be visible, or a building becomes
        // a collection of gaps.
        w: Math.max(w, 1),
        h: Math.max(h, 1),
        top,
        fill: cssHexScaled(rgb, GROUND_SHADE + (1 - GROUND_SHADE) * t),
      });
    }
    rects.sort(byTop);
    for (const r of rects) {
      ctx.fillStyle = r.fill;
      ctx.fillRect(r.x, r.y, r.w, r.h);
    }

    ctx.lineWidth = Math.max(1, u);
    ctx.strokeStyle = BOUNDS_STROKE;
    ctx.strokeRect(bx, by, bw, bh);

    // --- Extract zones: drawn before the POIs, first claim on label space.
    const extractColor = cssHex(EXTRACT_EDGE_COLOR);
    const extractAnchors: { cx: number; y0: number; y1: number; name: string }[] = [];
    for (const ex of map.extracts) {
      const z = ex.zone;
      let x0 = this.projX(z.min.x);
      let y0 = this.projY(z.min.z);
      let x1 = this.projX(z.max.x);
      let y1 = this.projY(z.max.z);
      const grow = MIN_EXTRACT_U * u;
      if (x1 - x0 < grow) {
        const mid = (x0 + x1) * 0.5;
        x0 = mid - grow * 0.5;
        x1 = mid + grow * 0.5;
      }
      if (y1 - y0 < grow) {
        const mid = (y0 + y1) * 0.5;
        y0 = mid - grow * 0.5;
        y1 = mid + grow * 0.5;
      }
      ctx.fillStyle = EXTRACT_FILL;
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      ctx.lineWidth = Math.max(1.5, 2 * u);
      ctx.strokeStyle = extractColor;
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
      extractAnchors.push({ cx: (x0 + x1) * 0.5, y0, y1, name: ex.name });
    }

    // --- POI dots ----------------------------------------------------------
    // Dots for every POI unconditionally; only the *names* are ever dropped.
    const pois = map.pois ?? [];
    const dotR = POI_DOT_U * u;
    for (const poi of pois) {
      const x = this.projX(poi.pos.x);
      const y = this.projY(poi.pos.z);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      ctx.beginPath();
      ctx.arc(x, y, dotR, 0, Math.PI * 2);
      ctx.fillStyle = cssHex(poiColor(poi.kind));
      ctx.fill();
      ctx.lineWidth = Math.max(1, u);
      ctx.strokeStyle = TEXT_HALO;
      ctx.stroke();
    }

    // --- Names: one occupancy list filled in priority order — chrome,
    // extracts,
    // then POI names that fit. A dropped name keeps its dot.
    const placed: Rect[] = this.chromeRects(u);

    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.font = `700 ${EXTRACT_TEXT_U * u}px ${MONO}`;
    const size = EXTRACT_TEXT_U * u;
    const pad = PAD_U * u;
    for (const a of extractAnchors) {
      // Above the zone unless off the plate, then below, nudged sideways until
      // it fits. Never dropped.
      let ty = a.y0 - size * 0.8;
      if (ty - size * 0.62 < pad) ty = a.y1 + size * 0.8;
      const half = ctx.measureText(a.name).width * 0.5;
      const tx = clamp(a.cx, pad + half, this.sidePx - pad - half);
      placed.push(textRect(ctx, a.name, tx, ty, "center", size));
      drawText(ctx, a.name, tx, ty, "center", extractColor, u);
    }

    ctx.font = `600 ${POI_TEXT_U * u}px ${MONO}`;
    for (const poi of pois) {
      this.drawPoiName(ctx, poi, placed, u);
    }

    this.drawChrome(ctx, map, u);
  }

  /**
   * A POI's name beside its dot. Four placements — right, left, above, below —
   * before giving up (twelve of Rietz's fourteen fit, against ten from the
   * right slot alone). The dot is drawn either way.
   */
  private drawPoiName(
    ctx: CanvasRenderingContext2D,
    poi: MapPoi,
    placed: Rect[],
    u: number,
  ): void {
    const x = this.projX(poi.pos.x);
    const y = this.projY(poi.pos.z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || poi.name.length === 0) return;

    const gap = (POI_DOT_U + 3) * u;
    const size = POI_TEXT_U * u;
    const pad = PAD_U * u;
    const stack = gap + size * 0.6;
    const candidates: { x: number; y: number; align: CanvasTextAlign }[] = [
      { x: x + gap, y, align: "left" },
      { x: x - gap, y, align: "right" },
      { x, y: y - stack, align: "center" },
      { x, y: y + stack, align: "center" },
    ];

    for (const c of candidates) {
      const rect = textRect(ctx, poi.name, c.x, c.y, c.align, size);
      if (rect.x0 < pad || rect.x1 > this.sidePx - pad) continue;
      if (rect.y0 < pad || rect.y1 > this.sidePx - pad) continue;
      if (placed.some((p) => overlaps(p, rect))) continue;
      placed.push(rect);
      drawText(ctx, poi.name, c.x, c.y, c.align, cssHex(poiColor(poi.kind)), u);
      return;
    }
  }

  // ---------------------------------------------------------------------
  // Chrome: title, north, scale bar, keybind
  // ---------------------------------------------------------------------

  /** The four corners the chrome will occupy, reserved before any POI name. */
  private chromeRects(u: number): Rect[] {
    const side = this.sidePx;
    const pad = PAD_U * u;
    const band = 26 * u;
    return [
      { x0: 0, y0: 0, x1: side * 0.55, y1: pad + band },
      { x0: side * 0.72, y0: 0, x1: side, y1: pad + band },
      { x0: 0, y0: side - pad - band * 1.4, x1: side * 0.45, y1: side },
      { x0: side * 0.55, y0: side - pad - band, x1: side, y1: side },
    ];
  }

  private drawChrome(ctx: CanvasRenderingContext2D, map: MapDef, u: number): void {
    const side = this.sidePx;
    const pad = PAD_U * u;

    ctx.textBaseline = "middle";
    ctx.font = `700 ${TITLE_TEXT_U * u}px ${MONO}`;
    drawText(ctx, map.name, pad, pad + TITLE_TEXT_U * u * 0.7, "left", CHROME_COLOR, u);

    // ...and the sky it is fought under, in the dim chrome tone, inside the
    // top-left rectangle `chromeRects` reserves.
    ctx.font = `700 ${CHROME_TEXT_U * u}px ${MONO}`;
    drawText(
      ctx,
      envLabel(this.env),
      pad,
      pad + TITLE_TEXT_U * u * 0.7 + CHROME_TEXT_U * u * 1.5,
      "left",
      CHROME_DIM_COLOR,
      u,
    );

    // North. The whole plan is oriented −Z up, and this is the promise of it.
    ctx.font = `700 ${CHROME_TEXT_U * u}px ${MONO}`;
    const nx = side - pad - 6 * u;
    const ny = pad + 22 * u;
    ctx.beginPath();
    ctx.moveTo(nx, ny - 16 * u);
    ctx.lineTo(nx - 5 * u, ny - 4 * u);
    ctx.lineTo(nx + 5 * u, ny - 4 * u);
    ctx.closePath();
    ctx.fillStyle = CHROME_COLOR;
    ctx.fill();
    drawText(ctx, "N", nx, ny + 6 * u, "center", CHROME_COLOR, u);

    // Scale bar — the one thing that turns "over there" into "400 metres".
    const b = map.bounds;
    const span = Math.max(b.max.x - b.min.x, b.max.z - b.min.z);
    let bar: number = SCALE_STEPS[0];
    for (const step of SCALE_STEPS) {
      if (step <= span / 5) bar = step;
    }
    const barPx = bar * this.scale;
    const y = side - pad - 8 * u;
    ctx.strokeStyle = CHROME_COLOR;
    ctx.lineWidth = Math.max(1.5, 2 * u);
    ctx.beginPath();
    ctx.moveTo(pad, y);
    ctx.lineTo(pad + barPx, y);
    ctx.moveTo(pad, y - 5 * u);
    ctx.lineTo(pad, y + 5 * u);
    ctx.moveTo(pad + barPx, y - 5 * u);
    ctx.lineTo(pad + barPx, y + 5 * u);
    ctx.stroke();
    drawText(ctx, `${bar} m`, pad, y - 14 * u, "left", CHROME_DIM_COLOR, u);

    drawText(
      ctx,
      "M — Close map",
      side - pad,
      side - pad - 8 * u,
      "right",
      CHROME_DIM_COLOR,
      u,
    );
  }

  // ---------------------------------------------------------------------
  // Live layer
  // ---------------------------------------------------------------------

  private drawPlayer(pos: Readonly<Vec3>, yaw: number): void {
    const ctx = this.live?.getContext("2d") ?? null;
    if (ctx === null) return;

    const side = this.sidePx;
    const u = side / 1000;

    const prev = this.lastArrow;
    if (prev !== null) {
      ctx.clearRect(prev.x0, prev.y0, prev.x1 - prev.x0, prev.y1 - prev.y0);
      this.lastArrow = null;
    }

    if (!Number.isFinite(pos.x) || !Number.isFinite(pos.z) || !Number.isFinite(yaw)) {
      return;
    }

    const len = PLAYER_LEN_U * u;
    const wid = PLAYER_WIDTH_U * u;
    const reach = len + wid + 4 * u;
    // Noclip and a bad snapshot can both put the player outside the bounds;
    // pinning the arrow beats losing it.
    const x = clamp(this.projX(pos.x), reach, side - reach);
    const y = clamp(this.projY(pos.z), reach, side - reach);

    // Yaw 0 faces −Z (up the screen), increasing yaw turns left —
    // `stepMovement`'s
    // convention. Forward is (−sin, −cos); right is forward a quarter turn
    // clockwise.
    const fx = -Math.sin(yaw);
    const fy = -Math.cos(yaw);
    const rx = -fy;
    const ry = fx;

    ctx.beginPath();
    ctx.moveTo(x + fx * len, y + fy * len);
    ctx.lineTo(x - fx * len * 0.55 + rx * wid, y - fy * len * 0.55 + ry * wid);
    ctx.lineTo(x - fx * len * 0.18, y - fy * len * 0.18);
    ctx.lineTo(x - fx * len * 0.55 - rx * wid, y - fy * len * 0.55 - ry * wid);
    ctx.closePath();
    ctx.fillStyle = PLAYER_FILL;
    ctx.fill();
    ctx.lineWidth = Math.max(1.5, 2 * u);
    ctx.lineJoin = "round";
    ctx.strokeStyle = PLAYER_STROKE;
    ctx.stroke();

    this.lastArrow = {
      x0: x - reach,
      y0: y - reach,
      x1: x + reach,
      y1: y + reach,
    };
  }

  private clearLive(): void {
    const ctx = this.live?.getContext("2d") ?? null;
    if (ctx === null) return;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.sidePx, this.sidePx);
    this.lastArrow = null;
  }
}

// ---------------------------------------------------------------------------

function byTop(a: PlanRect, b: PlanRect): number {
  return a.top - b.top;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clamp(v: number, lo: number, hi: number): number {
  if (!(hi > lo)) return (lo + hi) * 0.5;
  return v < lo ? lo : v > hi ? hi : v;
}

function overlaps(a: Rect, b: Rect): boolean {
  return a.x0 < b.x1 && b.x0 < a.x1 && a.y0 < b.y1 && b.y0 < a.y1;
}

/**
 * The box a label would occupy. `size` is the font's pixel size — the em height
 * plus padding is a better declutter test than glyph extents.
 */
function textRect(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  align: CanvasTextAlign,
  size: number,
): Rect {
  const w = ctx.measureText(text).width;
  const half = size * 0.62;
  const x0 = align === "left" ? x : align === "right" ? x - w : x - w * 0.5;
  return { x0: x0 - size * 0.2, y0: y - half, x1: x0 + w + size * 0.2, y1: y + half };
}

/** Halo first, then the glyphs: legible over plaster, grass and shadow alike. */
function drawText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  align: CanvasTextAlign,
  color: string,
  u: number,
): void {
  ctx.textAlign = align;
  ctx.lineJoin = "round";
  ctx.lineWidth = Math.max(2, 3.2 * u);
  ctx.strokeStyle = TEXT_HALO;
  ctx.strokeText(text, x, y);
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
}

/** True while the event's target is somewhere the player is typing. */
function isTextEntry(target: EventTarget | null): boolean {
  if (typeof HTMLElement === "undefined" || !(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}
